import type { ReactElement } from 'react'
import styled from 'styled-components'

import { SectionTitle } from '@/components/elements/SectionTitle'
import { Spacer } from '@/components/elements/Spacer'
import { TitleLayout } from '@/components/enlayout'
import { Head } from '@/components/enlayouts/Head'
import { Layout } from '@/components/enlayouts/Ir'
import { AHeading, CHeading } from '@/components/enlayouts/Ir/Heading'
import { Paragraph } from '@/components/enlayouts/Ir/Paragraph'

export const InternalControlPage = () => {
  return (
    <Layout>
      <SectionTitle
        color="normal"
        enTitleText="Internal Control Structure"
        subTitleText=""
      />
      <Spacer h={{ sp: 70, pc: 120 }} />
      <section>
        <AHeading>Status of Internal Control System</AHeading>
        <Paragraph>
          The Company recognizes that the appropriate establishment and operation of an internal control system is an important management issue to ensure the fairness and efficiency of business execution. The Company&apos;s Board of Directors has resolved on a basic policy regarding the internal control system.
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 80 }} />
        <CHeading>
          1. System to ensure that the execution of duties by Directors complies with laws and regulations and the Articles of Incorporation
        </CHeading>
        <List>
          <Paragraph>
            (1)
            The Directors shall ensure that the Company and its subsidiaries (hereinafter referred to as the &quot;TRIAL Group&quot;) are fully aware of the importance of establishing and observing corporate ethics within the TRIAL Group through the &quot;TRIAL Code of Conduct,&quot; which expresses the rules and ideas to be shared by the Company and its subsidiaries. 
          </Paragraph>
          <Paragraph>
            (2)
            The Board of Directors shall consist of directors, including outside directors, and shall make decisions on important matters and supervise the execution of duties by directors in accordance with laws and regulations, the Articles of Incorporation, the &quot;Board of Directors Regulations&quot; and other internal regulations.
          </Paragraph>
          <Paragraph>
            (3)
            Corporate auditors shall audit the appropriateness of the execution of duties by directors in accordance with the &quot;Audit & Supervisory Board Regulations&quot; and the &quot;Code of Audit and Supervisory Board Member Auditing Standards.&quot;
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          2.  System for the storage and management of information related to the execution of duties by directors
        </CHeading>
        <List>
          <Paragraph>
            (1)
            With regard to information security, based on the &quot;Information Security Policy&quot; and the &quot;Information Security Policy Operation Regulations,&quot; the TRIAL Group shall clarify the system of responsibility for information security and establish an information security management implementation system that continuously implements measures for the maintenance and improvement of information security. The &quot;Information Security Committee&quot; shall deliberate on the information security and promote it across the entire group.
          </Paragraph>
          <Paragraph>
            (2)
            The Company shall prepare approval documents, minutes of general meetings of shareholders, minutes of Board of Directors meetings, and other documents required for directors, executive officers, and employees to execute their duties in accordance with the Company&apos;s internal rules and regulations.
          </Paragraph>
          <Paragraph>
            (3)
            Documents (including electromagnetic information) shall be stored and managed in accordance with the Document Management Rules.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>3. Regulations and other systems for managing risk of loss</CHeading>
        <Paragraph>
          The company has established the Group Risk and Compliance Committee, which is responsible for preventing potential losses and minimizing damage during a crisis, and has formulated the &quot;Group Risk Management Regulations&quot; to strive for the reduction of loss risks and damages. Furthermore, by conducting risk assessments for each business division and managing the risk of losses for the entire group, we not only aim to provide safety and security to stakeholders and prevent the impairment of brand value, but also to enhance corporate value.
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          4. System to ensure the efficient execution of duties by directors
        </CHeading>
        <List>
          <Paragraph>
            (1)
            The Board of Directors shall meet once a month, or as necessary, on an as-needed basis, and the status of directors&apos; execution shall be reported to the Board of Directors once a quarter.
          </Paragraph>
          <Paragraph>
            (2)
            With respect to business execution, the Company shall clarify management responsibility by granting the necessary authority for each business execution in accordance with the predetermined &quot;Policies of Dividing Duties,&quot; &quot;Policies of Administrative Authority,&quot; and &quot;Rules for Management of Affiliated Companies.&quot;
          </Paragraph>
          <Paragraph>
            (3)
            The Company shall establish a medium-term management plan for the Group, disseminate it to officers and employees of the Group, and set numerical targets for each business field to achieve the plan.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          5. System to ensure that the execution of duties by employees complies with laws and regulations and the Articles of Incorporation
        </CHeading>
        <List>
          <Paragraph>
            (1) The Company shall establish and enforce &quot;TRIAL&apos;s Code of Conduct&quot; to ensure that each and every employee recognizes the importance of compliance and complies with laws and regulations in order to meet the demands of our stakeholders, including customers, business partners, shareholders, and employees, who surround our group.
          </Paragraph>
          <Paragraph>
            (2) The Internal Audit Department has been established as a department under the direct control of the Representative Director to conduct internal audits. In the event that an Internal Audit Office audit reveals any violation of laws, the Articles of Incorporation, or internal regulations, the Internal Audit Office shall immediately report the matter to the President and Representative Director.
          </Paragraph>
          <Paragraph>
            (3) An internal system shall be established to enable employees to report to the compliance promoter any violation of laws, regulations, the Articles of Incorporation, or internal rules that they discover.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          6. System to ensure the appropriateness of business operations in our group
        </CHeading>
        <List>
          <Paragraph>
            (1)
            When a director engages in a transaction that may cause a substantial conflict of interest with the Company for himself/herself or for a parent company, subsidiary, or other third party, he/she shall discuss the details of the transaction at a meeting of the Board of Directors and obtain approval from the Board of Directors before implementing it.
          </Paragraph>
          <Paragraph>
            (2) 
            When conducting transactions with related parties such as the Company&apos;s board members and major shareholders, the details of the transaction shall be discussed by the Related Party Transaction Exploratory Committee in accordance with the &quot;Related Party Transaction Management Regulations,&quot; and the department in charge shall execute the transaction after obtaining approval at the Board of Directors meeting. In addition, a report on the annual transaction results with each related party, etc. shall be made once a year, and the rationality and appropriateness of the transaction shall be carefully examined. When passing a resolution of the Board of Directors regarding a transaction, the fairness of the procedure shall be ensured by, for example, excluding directors who have an interest in the transaction as special interested parties.
          </Paragraph>
          <Paragraph>
            (3)
            The Internal Audit Department shall audit the appropriateness of the Group&apos;s operations. However, for subsidiaries that have their own internal audit departments, the Internal Audit Office shall conduct audits in cooperation with such departments.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          7.
          Matters concerning employees to assist the auditors in the event that the auditors request the appointment of such employees, matters concerning the independence of such employees from the directors of the Company, and matters concerning the execution of instructions given to such employees.
        </CHeading>
        <List>
          <Paragraph>
            (1)
            No dedicated employee shall be appointed, and the Corporate Auditors may, upon consultation with the General Manager of the Internal Audit Office, order the staff of the Internal Audit Office to perform matters necessary for audit duties.
          </Paragraph>
          <Paragraph>
            (2)
            The authority to direct and order the staff appointed in accordance with the preceding paragraph shall be transferred to the auditors, and they shall not receive any direction or order from the directors, internal audit office, etc.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          8.
          System for Directors and Employees to Report to Corporate Auditors and Other Systems Related to Reporting to Corporate Auditors
        </CHeading>
        <List>
          <Paragraph>
            (1)
            Directors and employees shall report from time to time on the status of execution of their duties and other matters as requested by the Corporate Auditors.
          </Paragraph>
          <Paragraph>
            (2)
            Corporate auditors may attend meetings of the Board of Directors, the Group Risk Compliance Committee, and other important meetings, hear from directors and others about the status of the execution of their duties, and inspect related materials.
          </Paragraph>
          <Paragraph>
            (3)
            The Company shall instruct directors, executive officers, and employees of subsidiaries to report from time to time on the status of performance of their duties and other matters as requested by the Corporate Auditors.
          </Paragraph>
          <Paragraph>
            (4)
            Employees may directly report to Corporate Auditors facts, etc. that may cause significant damage to the Company.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          9.
          System to ensure that a person who makes a report to an auditor is not subjected to unjust treatment on the basis of such report 
        </CHeading>
        <Paragraph>
          The &quot;Internal Reporting Regulations&quot; shall clearly state that any person who reports to the Corporate Auditors in accordance with the above shall be prohibited from being treated unfairly and shall be fully communicated to all employees.
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          10.
          Matters concerning procedures for prepayment or reimbursement of expenses incurred in the execution of duties by Corporate Auditors and other policies concerning the treatment of expenses or liabilities incurred in the execution of such duties
        </CHeading>
        <Paragraph>
          When a Corporate Auditor requests the Company for advance payment of expenses, etc. under Article 388 of the Companies Act in connection with the performance of his/her duties, the relevant expenses or liabilities shall be promptly processed after confirmation by the department in charge.
        </Paragraph>
        <Spacer h={{ sp: 50, pc: 50 }} />
        <CHeading>
          11. Other systems to ensure that audits by corporate auditors are conducted effectively
        </CHeading>
        <List>
          <Paragraph>
            (1)
            Corporate auditors shall attend meetings of the Board of Directors and other important meetings as deemed necessary.
          </Paragraph>
          <Paragraph>
            (2)
            In addition to the regular monthly meetings of the Audit & Supervisory Board, the Audit & Supervisory Board  shall hold extraordinary meetings as necessary to exchange information and discuss the status of audits, etc., and shall receive reports on accounting audits from the accounting auditors on a regular basis and exchange opinions with them.
          </Paragraph>
          <Paragraph>
            (3)
            The Internal Audit Department shall closely exchange information and collaborate with the Corporate Auditors on the formulation of internal audit plans for each fiscal year, internal audit results, etc.
          </Paragraph>
          <Paragraph>
            (4)
            Corporate auditors may independently engage outside experts when deemed necessary in conducting audits.
          </Paragraph>
        </List>
        <Spacer h={{ sp: 70, pc: 100 }} />
        <AHeading>Status of Establishing the Risk Management System</AHeading>
        <Paragraph>
          We are working to develop a systematic risk management system that accurately identifies, analyzes, and addresses risks ranging from natural disasters to man-made risks, internal and external risks, etc., by capturing risks in all aspects of operations, management, markets, and credit in each business unit, including preventative risk management.
        </Paragraph>
        <Paragraph>
          In addition, the Group Risk Compliance Committee holds a regular quarterly meeting to check the status of compliance with the compliance system, and ensures that each business unit complies with laws and regulations and avoids legal risks, and reports to the Board of Directors.
        </Paragraph>
      </section>
    </Layout>
  )
}

InternalControlPage.getLayout = (page: ReactElement) => {
  const breadCrumbs = [
    { name: 'Home', path: '/en/' },
    { name: 'Investor Relations', path: '/en/ir/' },
    { name: 'Corporate Governance', path: '/en/ir/governance/' },
    { name: 'Internal Control Structure', path: '' },
  ]

  return (
    <>
      <Head
        title="Internal Control Structure"
        description="Information on the status of establishing internal control systems and risk management frameworks."
        ogp={{ url: 'en/ir/governance/internal-control/' }}
      />
      <TitleLayout
        title="Investor Relations"
        subTitle=""
        breadCrumbs={breadCrumbs}
      >
        {page}
      </TitleLayout>
    </>
  )
}

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`
